import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Redirect, Route, Router, Switch } from 'wouter';
import { InstallPrompt } from './components/InstallPrompt';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RoleGuard } from './components/RoleGuard';
import { AuthProvider } from './lib/AuthContext';
import { ToastProvider } from './lib/ToastContext';
import { AttendancePage } from './pages/AttendancePage';
import { AuditLogPage } from './pages/AuditLogPage';
import { BranchesPage } from './pages/BranchesPage';
import { BrandingSettingsPage } from './pages/BrandingSettingsPage';
import { DashboardPage } from './pages/DashboardPage';
import { HolidaysPage } from './pages/HolidaysPage';
import { LeavePage } from './pages/LeavePage';
import { LeaveTypesPage } from './pages/LeaveTypesPage';
import { LoansPage } from './pages/LoansPage';
import { LoginPage } from './pages/LoginPage';
import { MyPayslipsPage } from './pages/MyPayslipsPage';
import { PayrollPage } from './pages/PayrollPage';
import { PayrollRunPage } from './pages/PayrollRunPage';
import { PayrollSettingsPage } from './pages/PayrollSettingsPage';
import { ProfilePage } from './pages/ProfilePage';
import { RegionsPage } from './pages/RegionsPage';
import { ReportsPage } from './pages/ReportsPage';
import { SalaryPage } from './pages/SalaryPage';
import { SettingsPage } from './pages/SettingsPage';
import { ShiftsPage } from './pages/ShiftsPage';
import { StaffDetailPage } from './pages/StaffDetailPage';
import { StaffListPage } from './pages/StaffListPage';
import { TaskDetailPage } from './pages/TaskDetailPage';
import { TasksPage } from './pages/TasksPage';
import { TeamPage } from './pages/TeamPage';

const client = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 15_000 } } });
const managerRoles = ['branch_head', 'region_head', 'general_head', 'hr', 'admin'] as const;
const protectedPage = (node: ReactNode) => <ProtectedRoute>{node}</ProtectedRoute>;
const guarded = (roles: (typeof managerRoles)[number][], node: ReactNode) => protectedPage(<RoleGuard roles={roles}>{node}</RoleGuard>);

export function App() {
  return (
    <QueryClientProvider client={client}>
      <ToastProvider>
        <Router>
          <AuthProvider>
            <InstallPrompt />
            <Switch>
              <Route path="/login"><LoginPage /></Route>
              <Route path="/">{protectedPage(<DashboardPage />)}</Route>
              <Route path="/attendance">{protectedPage(<AttendancePage />)}</Route>
              <Route path="/team">{guarded([...managerRoles], <TeamPage />)}</Route>
              <Route path="/profile">{protectedPage(<ProfilePage />)}</Route>
              <Route path="/leave">{protectedPage(<LeavePage />)}</Route>
              <Route path="/tasks">{protectedPage(<TasksPage />)}</Route>
              <Route path="/tasks/:id">{(params) => protectedPage(<TaskDetailPage id={params.id} />)}</Route>
              <Route path="/loans">{protectedPage(<LoansPage />)}</Route>
              <Route path="/salary">{guarded(['hr', 'admin'], <SalaryPage />)}</Route>
              <Route path="/payroll">{guarded(['hr', 'admin'], <PayrollPage />)}</Route>
              <Route path="/payroll/:id">{(params) => guarded(['hr', 'admin'], <PayrollRunPage id={params.id} />)}</Route>
              <Route path="/my-payslips">{protectedPage(<MyPayslipsPage />)}</Route>
              <Route path="/reports">{guarded([...managerRoles], <ReportsPage />)}</Route>
              <Route path="/audit-log">{guarded(['admin'], <AuditLogPage />)}</Route>
              <Route path="/staff">{guarded([...managerRoles], <StaffListPage />)}</Route>
              <Route path="/staff/:id">{(params) => guarded([...managerRoles], <StaffDetailPage id={params.id} />)}</Route>
              <Route path="/settings">{guarded(['hr', 'admin'], <SettingsPage />)}</Route>
              <Route path="/settings/regions">{guarded(['hr', 'admin'], <RegionsPage />)}</Route>
              <Route path="/settings/branches">{guarded(['hr', 'admin'], <BranchesPage />)}</Route>
              <Route path="/settings/shifts">{guarded(['hr', 'admin'], <ShiftsPage />)}</Route>
              <Route path="/settings/holidays">{guarded(['hr', 'admin'], <HolidaysPage />)}</Route>
              <Route path="/settings/leave-types">{guarded(['hr', 'admin'], <LeaveTypesPage />)}</Route>
              <Route path="/settings/payroll">{guarded(['hr', 'admin'], <PayrollSettingsPage />)}</Route>
              <Route path="/settings/branding">{guarded(['hr', 'admin'], <BrandingSettingsPage />)}</Route>
              <Route><Redirect to="/" /></Route>
            </Switch>
          </AuthProvider>
        </Router>
      </ToastProvider>
    </QueryClientProvider>
  );
}
